import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, Monitor } from 'lucide-react';
import { navFor } from '@nu/shared';
import SyncIndicator from '../components/SyncIndicator.jsx';
import NavIcon from '../components/NavIcon.jsx';
import { signOut } from '../auth/msal.js';
import UpdateButton from '../components/UpdateButton.jsx';
import { useUnreadCount } from '../modules/comms/inbox.js';

// Phone-first frame: header (menu left, badge centred, sync status right),
// content, bottom tabs within thumb reach. Account and app actions live in
// the slide-out menu so the tabs stay for places, not actions.
export default function FieldShell({ me, onSwitchShell }) {
  const items = navFor(me.capabilities, 'field');
  const [menuOpen, setMenuOpen] = useState(false);
  const unread = useUnreadCount();
  const badge = (i) => (i.id === 'messages' ? unread : 0);
  const close = () => setMenuOpen(false);
  useKeyboardOpen();
  // A tab stays lit on its other paths too (Support for a ticket).
  const { pathname } = useLocation();
  const lit = (i) => ({ isActive }) => (isActive || (i.also || []).some((p) => pathname.startsWith(p)) ? 'active' : '');

  return (
    <div className="field-shell">
      <header className="field-head">
        <button type="button" className="icon-btn" aria-label="Menu" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}>
          <Menu size={26} aria-hidden="true" />
        </button>
        <img className="logo" src="/brand/nano-utilities-badge.svg" alt="Nano Utilities" />
        <SyncIndicator />
      </header>

      <main className="field-main"><Outlet /></main>

      <nav className="tabbar" aria-label="Main">
        {items.filter((i) => !i.menuOnly).map((i) => (
          <NavLink key={i.id} to={i.path} end={i.path === '/'} className={(s) => `tab ${lit(i)(s)}`}>
            <NavIcon name={i.icon} badge={badge(i)} />
            <span>{i.label}</span>
          </NavLink>
        ))}
      </nav>

      {menuOpen && (
        <>
          <div className="scrim" onClick={close} />
          <aside className="drawer" role="dialog" aria-modal="true" aria-label="Menu">
            <div className="drawer-head">
              <img className="drawer-badge" src="/brand/nano-utilities-logo-white.svg" alt="Nano Utilities" />
              <button type="button" className="icon-btn" aria-label="Close menu" onClick={close}>
                <X size={26} aria-hidden="true" />
              </button>
            </div>
            <div className="drawer-who">
              <span className="avatar" aria-hidden="true">{initials(me.name)}</span>
              <strong>{me.name}</strong>
              <span>{me.email}</span>
            </div>
            <nav className="drawer-links" aria-label="Menu">
              {items.map((i) => (
                <NavLink key={i.id} to={i.path} end={i.path === '/'} className={(s) => `drawer-link ${lit(i)(s)}`} onClick={close}>
                  <NavIcon name={i.icon} size={22} badge={badge(i)} />
                  <span>{i.label}</span>
                </NavLink>
              ))}
            </nav>
            <div className="drawer-foot">
              {onSwitchShell && (
                <button type="button" className="drawer-link" onClick={() => { close(); onSwitchShell(); }}>
                  <Monitor size={22} aria-hidden="true" />
                  <span>Office view</span>
                </button>
              )}
              <UpdateButton className="drawer-link" size={22} />
              <button type="button" className="drawer-link" onClick={signOut}>
                <LogOut size={22} aria-hidden="true" />
                <span>Sign out</span>
              </button>
            </div>
          </aside>
        </>
      )}
    </div>
  );
}

function initials(name = '') {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

// body.kb-open while a text field has focus AND the phone's keyboard is up
// (the visible screen has shrunk by a quarter or more), so the bottom tabs
// step aside instead of riding on top of the keyboard. No on-screen keyboard
// (a computer, a tablet with a keyboard): the tabs stay.
const TYPING = 'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="tel"], input[type="number"], input[type="url"], input[type="password"]';
function useKeyboardOpen() {
  useEffect(() => {
    const vv = window.visualViewport;
    const height = () => (vv ? vv.height : window.innerHeight);
    let tallest = height();
    let width = window.innerWidth;
    let t = null;
    const check = () => {
      // Turned the phone round: start again from this height.
      if (window.innerWidth !== width) { width = window.innerWidth; tallest = height(); }
      tallest = Math.max(tallest, height());
      const typing = document.activeElement?.matches?.(TYPING);
      document.body.classList.toggle('kb-open', Boolean(typing) && height() < tallest * 0.75);
    };
    // A short wait: the keyboard takes a moment to come up, and moving from
    // one field to the next shouldn't flash the tabs.
    const later = () => { clearTimeout(t); t = setTimeout(check, 150); };
    document.addEventListener('focusin', later);
    document.addEventListener('focusout', later);
    (vv || window).addEventListener('resize', check);
    return () => {
      document.removeEventListener('focusin', later);
      document.removeEventListener('focusout', later);
      (vv || window).removeEventListener('resize', check);
      document.body.classList.remove('kb-open');
      clearTimeout(t);
    };
  }, []);
}
